<template>
  <div>
    <div class="d-flex justify-content-between">
      <h1 class="page-header">{{ $t('wholesaleReceipt.pageTitle') }}</h1>
      <ts-breadcrumb
        :items="[
          { text: $t('home'), href: '/' },
          {
            text: $t('wholesaleReceipt.pageTitle'),
            href: '/admin/wholesale-receipts'
          },
          {
            text: $t('create'),
            active: true
          }
        ]"
      />
    </div>
    <ts-card>
      <ts-card-wrapper>
        <div class="row">
          <div class="col-md-4">
            <ts-loading-banner :loading="loading">
              <div
                class="ts-bg-gray-200 ts-border-blue-500 ts-rounded-md ts-p-2 ts-space-y-3"
              >
                <div class="row">
                  <div class="col-md-12">
                    <label class="required">{{
                      $t('wholesaleReceipt.date')
                    }}</label>
                    <a-date-picker
                      v-model="model.payment_date"
                      format="DD-MM-YYYY"
                      valueFormat="DD-MM-YYYY"
                      :disabled-date="disabledDate"
                      class="ts-w-full"
                    ></a-date-picker>
                    <div class="validate" v-if="errors.has('payment_date')">
                      {{ errors.first('payment_date') }}
                    </div>
                  </div>
                </div>
                <div class="row">
                  <div class="col-md-12">
                    <label>{{ $t('wholesaleReceipt.customer') }}</label>
                    <a-select
                      v-model="model.customer_id"
                      :options="customerList"
                      class="ts-w-full"
                      @change="onCustomerChange"
                      showSearch
                      :filterOption="
                        (input, option) =>
                          option.componentOptions.children[0].text
                            .toLowerCase()
                            .indexOf(input.toLowerCase()) >= 0
                      "
                    ></a-select>
                  </div>
                </div>
                <div class="row">
                  <div class="col-md-12">
                    <label>{{ $t('wholesaleReceipt.grandTotal') }}</label>
                    <input
                      v-model="model.invoice_amount"
                      type="text"
                      class="form-control"
                      readonly
                    />
                  </div>
                </div>
                <div class="row">
                  <div class="col-md-12">
                    <label class="required">{{
                      $t('wholesaleReceipt.amountPaid')
                    }}</label>
                    <input
                      v-model.number="model.amount_paid"
                      type="number"
                      class="form-control"
                    />
                    <div class="validate" v-if="errors.has('amount_paid')">
                      {{ errors.first('amount_paid') }}
                    </div>
                  </div>
                </div>
                <div class="row">
                  <div class="col-md-6">
                    <label class="required">{{
                      $t('wholesaleReceipt.fxRate')
                    }}</label>
                    <input
                      v-model.number="model.fx_rate"
                      type="number"
                      step="any"
                      class="form-control"
                      readonly
                    />
                  </div>
                  <div class="col-md-6">
                    <label :class="allowPick ? 'required' : ''">{{
                      $t('wholesaleReceipt.nextPayment')
                    }}</label>
                    <a-date-picker
                      v-model="model.next_payment_date"
                      :disabled="!allowPick"
                      format="DD-MM-YYYY"
                      valueFormat="DD-MM-YYYY"
                      class="ts-w-full"
                    ></a-date-picker>
                    <div
                      class="validate"
                      v-if="errors.has('next_payment_date')"
                    >
                      {{ errors.first('next_payment_date') }}
                    </div>
                  </div>
                </div>
              </div>
              <div class="row ts-mt-2">
                <div class="col-md-12">
                  <PaymentMethod
                    v-model="model"
                    :validate="errors"
                    @loading="value => (loading = value)"
                  />
                </div>
              </div>
              <div class="row ts-mt-2">
                <div class="col-md-12">
                  <div
                    class="ts-bg-gray-200 ts-border-blue-500 ts-rounded-md ts-p-2 ts-space-y-3"
                  >
                    <div class="form-group">
                      <label>{{ $t('wholesaleReceipt.remark') }}</label>
                      <textarea
                        v-model="model.remark"
                        rows="3"
                        class="form-control"
                      ></textarea>
                    </div>
                  </div>
                </div>
              </div>
              <br />
              <div class="row">
                <div class="col-md-12 ts-flex ts-space-x-3 ts-justify-end">
                  <ts-button
                    @click.prevent="
                      () => $router.push({ name: 'wholesale-receipt' })
                    "
                    >{{ $t('cancel') }}</ts-button
                  >
                  <ts-button
                    :waiting="waiting"
                    color="primary"
                    @click.prevent="onSave"
                    >{{ $t('save') }}</ts-button
                  >
                </div>
              </div>
            </ts-loading-banner>
          </div>
          <div class="col-md-8">
            <receipt-detail v-model="model" :validate="errors" ref="receipt" />
          </div>
        </div>
      </ts-card-wrapper>
    </ts-card>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import { Errors } from 'form-backend-validation'
import PaymentMethod from './components/payment-method.vue'
import ReceiptDetail from './components/receipt-detail.vue'
import moment from 'moment'
import { debounce } from 'lodash'

export default {
  name: 'wholesaleReceipt',
  components: {
    PaymentMethod,
    ReceiptDetail
  },
  data () {
    return {
      waiting: false,
      loading: false,
      src: '',
      errors: new Errors(),
      model: {
        payment_date: moment().format('DD-MM-YYYY'),
        payment_list: [],
        invoice_amount: '',
        fx_rate: '',
        next_payment_date: '',
        remark: '',
        payment_method_id: 1,
        account_number: null,
        account_name: null,
        bank_name: null,
        customer_id: null,
        amount_paid: null
      },
      allow_back_date_sale_inovice: null
    }
  },
  created () {
    this.model.fx_rate = parseFloat(
      this.$store.state.authUser.sys_setting.fx_rate
    )
    this.loading = true
    this.getAllowBackDateSaleInovice()
    this.fetchCustomer().finally(() => (this.loading = false))
  },
  computed: {
    ...mapState('wholesale/receipt', ['customers']),
    allowPick () {
      if (this.model.customer_id) {
        return true
      }
      return false
    },
    customerList () {
      if (this.customers) {
        return this.customers.map(el => ({
          value: el.customer_id,
          label: el.customer_name + '|' + el.contact_number
        }))
      }
      return []
    }
  },
  methods: {
    ...mapActions('wholesale/receipt', ['fetchCustomer', 'getFormViewData']),
    getAllowBackDateSaleInovice () {
      this.getFormViewData({
        params: {
          fnName: 'allowBackDateSaleInovice'
        }
      }).then(response => {
        this.allow_back_date_sale_inovice =
          response.data.allowBackDateSaleInovice.allow_back_date_sale_inovice
      })
    },
    disabledDate (current) {
      let current_date = new Date() // Current date and time
      let allow_date = new Date(current_date) // Create a new Date object with the current date and time
      allow_date.setDate(
        current_date.getDate() - this.allow_back_date_sale_inovice - 1
      )
      return current && current.valueOf() < allow_date
    },
    amountPaid () {
      this.$refs.receipt.calculationAmountPaid()
      this.model.next_payment_date = null
    },
    onSave () {
      this.waiting = true
      this.src = null
      this.$store
        .dispatch('wholesale/receipt/store', this.model)
        .then(response => {
          this.$toasted.success(response.message)
          this.$router.push({ name: 'wholesale-receipt' })
          this.clearInput()
        })
        .catch(error => {
          this.$toasted.error(error.message)
          this.errors = new Errors(error.errors)
        })
        .finally(() => {
          this.waiting = false
        })
    },
    onCustomerChange () {
      this.model.invoice_amount = ''
      this.model.amount_paid = null
      this.model.amount_due = 0
      this.model.next_payment_date = ''
      this.model.remark = ''
      this.model.payment_method_id = 1
      this.model.bank_name = null
      this.model.account_number = null
      this.model.account_name = null
    },
    clearInput () {
      this.model.payment_date = moment().format('DD-MM-YYYY')
      this.model.payment_list = []
      this.model.next_payment_date = ''
      this.model.remark = ''
      this.model.payment_method_id = 1
      this.model.customer_id = null
      this.model.account_number = null
      this.model.account_name = null
      this.model.bank_name = null
    }
  },
  watch: {
    'model.amount_paid': debounce(function () {
      this.amountPaid()
    }, 500)
  }
}
</script>
